import React, { useState, useEffect, useMemo } from 'react';
import { doc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase/config/firebase';
import { Booking, Trip, StudentProfile, University } from '../../types';
import { adminBookingService } from '../../services/adminBookingService';
import { isReturnTrip } from '../../services/tripService';
import { universityService } from '../../services/universityService';
import { AdminBookingCalendar } from './booking/AdminBookingCalendar';
import { AdminDirectBookingModal } from './booking/AdminDirectBookingModal';
import { AdminTripPassengerModal } from './booking/AdminTripPassengerModal';
import { AdminBookingActionModal } from './booking/AdminBookingActionModal';
import {
  Ticket,
  Search,
  Filter,
  Eye,
  XCircle,
  CheckCircle2,
  Clock,
  RefreshCw,
  QrCode,
  X,
  Trash2,
  Calendar,
  UserPlus,
  Repeat,
  MessageSquare,
} from 'lucide-react';
import { AdminLoadMore } from './AdminLoadMore';
import './AdminPages.css';

export const AdminBookings: React.FC = () => {
  // Navigation / View state
  const [viewMode, setViewMode] = useState<'CALENDAR' | 'TABLE'>('CALENDAR');

  // Core Data
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [trips, setTrips] = useState<Trip[]>([]);
  const [students, setStudents] = useState<StudentProfile[]>([]);
  const [universities, setUniversities] = useState<University[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Table Filters, Search & Visibility (15 initial + 10 more)
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [visibleCount, setVisibleCount] = useState<number>(15);

  // Modals State
  const [isDirectBookingModalOpen, setIsDirectBookingModalOpen] = useState(false);
  const [directBookingDefaultDate, setDirectBookingDefaultDate] = useState<string | undefined>(undefined);
  const [directBookingDefaultStudentUid, setDirectBookingDefaultStudentUid] = useState<string | undefined>(undefined);
  const [selectedTripForManifest, setSelectedTripForManifest] = useState<Trip | null>(null);
  const [actionBooking, setActionBooking] = useState<Booking | null>(null);
  const [selectedBookingDetails, setSelectedBookingDetails] = useState<Booking | null>(null);

  // Status & Feedback
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // Load all data
  const loadAllData = async (forceRefresh = false) => {
    setIsLoading(true);
    try {
      const [allBks, allTrps, allStds, allUnis] = await Promise.all([
        adminBookingService.getAllBookings(forceRefresh),
        adminBookingService.getAllTrips(forceRefresh),
        adminBookingService.getAllStudents(forceRefresh),
        universityService.getAllUniversities(),
      ]);

      setBookings(allBks);
      setTrips(allTrps);
      setStudents(allStds);
      setUniversities(allUnis);
    } catch (err) {
      console.warn('Error loading admin booking data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAllData();

    const handleSync = () => {
      loadAllData(true);
    };
    window.addEventListener('oto_data_sync', handleSync);
    window.addEventListener('oto_booking_deleted', handleSync);

    // Check if coming from students page with a preselected student
    if (typeof window !== 'undefined') {
      try {
        const raw = sessionStorage.getItem('nashgo_booking_target_student');
        if (raw) {
          const target = JSON.parse(raw);
          sessionStorage.removeItem('nashgo_booking_target_student');
          if (target?.uid) {
            setDirectBookingDefaultStudentUid(target.uid);
            setIsDirectBookingModalOpen(true);
          }
        }
      } catch (_) {}
    }

    return () => {
      window.removeEventListener('oto_data_sync', handleSync);
      window.removeEventListener('oto_booking_deleted', handleSync);
    };
  }, []);

  // Filtered bookings list for table view
  const filteredBookings = useMemo(() => {
    return bookings.filter((b) => {
      const matchesSearch =
        searchTerm === '' ||
        b.bookingCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        b.ticketId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        b.studentSnapshot?.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        b.studentSnapshot?.phoneNumber?.includes(searchTerm) ||
        b.universityName?.toLowerCase().includes(searchTerm.toLowerCase());

      const status = b.status || b.bookingStatus;
      const matchesStatus = statusFilter === 'ALL' || status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [bookings, searchTerm, statusFilter]);

  // Sliced bookings for table display (15 initial + 10 more on button click)
  const visibleBookings = useMemo(() => {
    return filteredBookings.slice(0, visibleCount);
  }, [filteredBookings, visibleCount]);

  // Reset visible count when filters change
  useEffect(() => {
    setVisibleCount(15);
  }, [searchTerm, statusFilter]);

  // Overall KPIs
  const stats = useMemo(() => {
    const total = bookings.length;
    const confirmed = bookings.filter((b) => (b.status || b.bookingStatus) === 'CONFIRMED').length;
    const pending = bookings.filter(
      (b) => (b.status || b.bookingStatus) === 'PENDING_PAYMENT_VERIFICATION'
    ).length;
    const postponed = bookings.filter((b) => (b.status || b.bookingStatus) === 'POSTPONED').length;
    const cancelled = bookings.filter((b) => (b.status || b.bookingStatus) === 'CANCELLED').length;
    return { total, confirmed, pending, postponed, cancelled };
  }, [bookings]);

  // Delete single booking handler
  const handleDeleteBooking = async (bookingId: string) => {
    const targetBooking = bookings.find((b) => b.id === bookingId);
    const code = targetBooking?.bookingCode || targetBooking?.ticketId || bookingId;
    if (!window.confirm(`هل أنت متأكد من حذف الحجز (${code}) نهائياً من قاعدة البيانات؟`)) {
      return;
    }

    setActionLoading(true);
    try {
      try {
        await deleteDoc(doc(db, 'bookings', bookingId));
      } catch (e) {
        console.warn('deleteDoc error:', e);
      }

      if (typeof window !== 'undefined') {
        try {
          sessionStorage.removeItem(`nashgo_dev_booking_${bookingId}`);
          localStorage.removeItem(`nashgo_dev_booking_${bookingId}`);
          const raw = sessionStorage.getItem('nashgo_dev_student_bookings');
          if (raw) {
            const devList = JSON.parse(raw) as Booking[];
            const updated = devList.filter((b) => b.id !== bookingId);
            sessionStorage.setItem('nashgo_dev_student_bookings', JSON.stringify(updated));
          }
        } catch (_) {}
      }

      setBookings((prev) => prev.filter((b) => b.id !== bookingId));
      if (selectedBookingDetails?.id === bookingId) {
        setSelectedBookingDetails(null);
      }
      setMessage({ text: `تم حذف الحجز (${code}) بنجاح.`, type: 'success' });
    } catch (err: any) {
      setMessage({ text: err?.message || 'حدث خطأ أثناء حذف الحجز', type: 'error' });
    } finally {
      setActionLoading(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  // Delete all bookings handler
  const handleDeleteAllBookings = async () => {
    if (bookings.length === 0) return;
    const confirmPrompt = window.confirm(
      `تحذير: هل أنت متأكد تماماً من حذف كافة الحجوزات البالغ عددها (${bookings.length}) نهائياً؟ هذا الإجراء لا يمكن التراجع عنه!`
    );
    if (!confirmPrompt) return;

    setActionLoading(true);
    try {
      for (const b of bookings) {
        try {
          await deleteDoc(doc(db, 'bookings', b.id));
        } catch (_) {}
      }

      if (typeof window !== 'undefined') {
        try {
          sessionStorage.removeItem('nashgo_dev_student_bookings');
          bookings.forEach((b) => {
            sessionStorage.removeItem(`nashgo_dev_booking_${b.id}`);
            localStorage.removeItem(`nashgo_dev_booking_${b.id}`);
          });
        } catch (_) {}
      }

      setBookings([]);
      setSelectedBookingDetails(null);
      setMessage({ text: 'تم حذف كافة الحجوزات وتصفير السجل بالكامل بنجاح.', type: 'success' });
    } catch (err: any) {
      setMessage({ text: err?.message || 'حدث خطأ أثناء حذف الحجوزات', type: 'error' });
    } finally {
      setActionLoading(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  return (
    <div className="admin-page-container" dir="rtl">
      {/* Page Header */}
      <div className="admin-page-header">
        <div className="admin-header-title-box">
          <h2>
            <Ticket size={26} className="text-[#E5232A]" />
            <span>مركز إدارة الحجوزات والعمليات اليومية</span>
          </h2>
          <p>
            تسكين وحجز التذاكر للطلاب المسجلين، تعديل وتأجيل وإلغاء الرحلات، ومتابعة إشغال المقاعد عبر النتيجة الشهرية التفاعلية.
          </p>
        </div>

        <div className="admin-header-actions-row flex items-center gap-2 flex-wrap">
          {/* Direct Booking for Student Button */}
          <button
            type="button"
            className="btn-admin-primary flex items-center gap-1.5 shadow-sm"
            onClick={() => {
              setDirectBookingDefaultDate(undefined);
              setIsDirectBookingModalOpen(true);
            }}
          >
            <UserPlus size={16} />
            <span>+ حجز رحلة لطالب مسجل</span>
          </button>

          <button
            type="button"
            className="btn-admin-outline"
            onClick={() => loadAllData(true)}
            disabled={isLoading}
            title="تحديث البيانات"
          >
            <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
            <span>تحديث</span>
          </button>

          <button
            type="button"
            className="btn-admin-danger flex items-center gap-1 text-xs py-2"
            onClick={handleDeleteAllBookings}
            disabled={actionLoading || bookings.length === 0}
            title="حذف كافة الحجوزات"
          >
            <Trash2 size={14} />
            <span>تصفير السجل ({bookings.length})</span>
          </button>
        </div>
      </div>

      {/* Alert Message */}
      {message && (
        <div
          className={`p-3 rounded-xl border flex items-center gap-2 ${
            message.type === 'success'
              ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
              : 'bg-red-50 text-red-800 border-red-200'
          }`}
        >
          <CheckCircle2 size={18} />
          <span>{message.text}</span>
        </div>
      )}

      {/* View Switcher Bar */}
      <div className="admin-view-toggle-bar">
        <div className="admin-segmented-control">
          <button
            type="button"
            className={`admin-segment-btn ${viewMode === 'CALENDAR' ? 'active' : ''}`}
            onClick={() => setViewMode('CALENDAR')}
          >
            <Calendar size={16} />
            <span>النتيجة والتقويم الشهري للرحلات والمقاعد</span>
          </button>

          <button
            type="button"
            className={`admin-segment-btn ${viewMode === 'TABLE' ? 'active' : ''}`}
            onClick={() => setViewMode('TABLE')}
          >
            <Ticket size={16} />
            <span>جدول الحجوزات والتذاكر الكامل</span>
          </button>
        </div>

        <div className="text-xs text-slate-500 font-medium">
          {viewMode === 'CALENDAR'
            ? 'اضغط على أي يوم أو رحلة في التقويم لإدارة المقاعد ونقل الركاب.'
            : `إجمالي السجلات: ${filteredBookings.length} حجز.`}
        </div>
      </div>

      {/* KPI Stats Strip */}
      <div className="admin-stats-strip">
        <div className="admin-stat-card">
          <div className="stat-icon blue">
            <Ticket size={22} />
          </div>
          <div className="stat-meta">
            <span className="stat-val font-mono">{stats.total}</span>
            <span className="stat-lbl">إجمالي الحجوزات</span>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="stat-icon green">
            <CheckCircle2 size={22} />
          </div>
          <div className="stat-meta">
            <span className="stat-val font-mono">{stats.confirmed}</span>
            <span className="stat-lbl">حجوزات مؤكدة</span>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="stat-icon amber">
            <Clock size={22} />
          </div>
          <div className="stat-meta">
            <span className="stat-val font-mono">{stats.pending}</span>
            <span className="stat-lbl">بانتظار السداد</span>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="stat-icon amber" style={{ background: '#fffbeb', color: '#b45309' }}>
            <Clock size={22} />
          </div>
          <div className="stat-meta">
            <span className="stat-val font-mono">{stats.postponed}</span>
            <span className="stat-lbl">رحلات مؤجلة</span>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="stat-icon red">
            <XCircle size={22} />
          </div>
          <div className="stat-meta">
            <span className="stat-val font-mono">{stats.cancelled}</span>
            <span className="stat-lbl">حجوزات ملغاة</span>
          </div>
        </div>
      </div>

      {/* VIEW 1: Interactive Monthly Calendar */}
      {viewMode === 'CALENDAR' && (
        <AdminBookingCalendar
          trips={trips}
          bookings={bookings}
          universities={universities}
          onSelectTrip={(t) => setSelectedTripForManifest(t)}
          onNewBookingClick={(defaultDate) => {
            setDirectBookingDefaultDate(defaultDate);
            setIsDirectBookingModalOpen(true);
          }}
        />
      )}

      {/* VIEW 2: Data Table */}
      {viewMode === 'TABLE' && (
        <>
          {/* Search & Filter Toolbar */}
          <div className="admin-toolbar-card">
            <div className="admin-search-wrapper">
              <Search size={18} />
              <input
                type="text"
                className="admin-search-input"
                placeholder="البحث بكود الحجز، التذكرة، اسم الطالب، رقم الهاتف..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="admin-filter-group">
              <Filter size={16} className="text-slate-400" />
              <select
                className="admin-filter-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="ALL">جميع الحالات</option>
                <option value="CONFIRMED">مؤكد (CONFIRMED)</option>
                <option value="PENDING_PAYMENT_VERIFICATION">بانتظار السداد</option>
                <option value="POSTPONED">مؤجل (POSTPONED)</option>
                <option value="CANCELLED">ملغي (CANCELLED)</option>
              </select>
            </div>
          </div>

          {/* Bookings Table */}
          <div className="admin-table-container">
            {isLoading ? (
              <div className="table-empty-state">
                <RefreshCw size={32} className="animate-spin text-[#E5232A]" />
                <p>جاري تحميل سجلات الحجوزات...</p>
              </div>
            ) : filteredBookings.length === 0 ? (
              <div className="table-empty-state">
                <Ticket size={40} />
                <p>لا توجد حجوزات مطابقة للفلاتر المحددة حاليًا.</p>
              </div>
            ) : (
              <table className="admin-data-table">
                <thead>
                  <tr>
                    <th>كود الحجز / التذكرة</th>
                    <th>الطالب المسافر</th>
                    <th>الجامعة والرحلة</th>
                    <th>التاريخ والوقت</th>
                    <th>المقعد</th>
                    <th>السعر والدفع</th>
                    <th>الحالة</th>
                    <th>إجراءات الإدارة والتعديل</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleBookings.map((b) => {
                    const status = b.status || b.bookingStatus;
                    const studentPhone = b.studentSnapshot?.phoneNumber;
                    const studentName = b.studentSnapshot?.fullName || 'طالبنا العزيز';
                    const isConfirmed = status === 'CONFIRMED' || b.paymentStatus === 'PAID';
                    const waConfirmMsg = isConfirmed
                      ? `مرحباً يا ${studentName} 👋\nتم تأكيد واعتماد حجز رحلتك بنجاح مع OTO Students! 🚌✨\n\n📋 *تفاصيل الحجز المؤكد:*\n• كود الحجز: *${b.bookingCode}*\n• رقم التذكرة: ${b.ticketId}\n• الوجهة: ${b.universityName}\n• تاريخ الرحلة: ${b.tripDate}\n• موعد الانطلاق: ${b.departureTime}\n• رقم المقعد: #${b.seatNumber}\n• المبلغ: ${b.price || 0} ج.م\n• حالة الحجز: *معتمد ومؤكد ✓*\n\n🎫 يمكنك عرض تذكرتك ومحطة الركوب عبر منصة الطلاب:\nhttps://otostudents.com\n\nنتمنى لك رحلة موفقة وآمنة مع OTO! 🌟`
                      : `مرحباً يا ${studentName}، بخصوص حجزك (${b.bookingCode}) لـ ${b.universityName} يوم ${b.tripDate}...`;
                    const waUrl = adminBookingService.getWhatsAppUrl(studentPhone, waConfirmMsg);

                    return (
                      <tr key={b.id}>
                        <td>
                          <div>
                            <strong className="font-mono text-[#E5232A] block">{b.bookingCode}</strong>
                            <span className="text-xs text-slate-400 font-mono">{b.ticketId}</span>
                          </div>
                        </td>
                        <td>
                          <div>
                            <strong className="block text-slate-800">
                              {b.studentSnapshot?.fullName || 'طالب مسجل'}
                            </strong>
                            <span className="text-xs text-slate-500 font-mono" dir="ltr">
                              {studentPhone || '—'}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div>
                            <strong className="block text-slate-800">{b.universityName}</strong>
                            <span
                              className={`dir-badge ${
                                isReturnTrip(b.direction) ? 'return' : 'outbound'
                              }`}
                            >
                              {isReturnTrip(b.direction) ? 'عودة للإسكندرية' : 'ذهاب للجامعة'}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div>
                            <span className="block font-bold">{b.tripDate}</span>
                            <span className="text-xs text-slate-500 font-mono">
                              {b.departureTime}
                            </span>
                          </div>
                        </td>
                        <td>
                          <span className="seat-number-tag font-bold">
                            مقعد #{b.seatNumber}
                          </span>
                        </td>
                        <td>
                          <div>
                            <strong className="block font-mono">{b.price} ج.م</strong>
                            <span className="text-xs text-slate-500">{b.paymentMethod}</span>
                          </div>
                        </td>
                        <td>
                          <span
                            className={`status-pill ${
                              status === 'CONFIRMED'
                                ? 'confirmed'
                                : status === 'CANCELLED'
                                ? 'cancelled'
                                : status === 'POSTPONED'
                                ? 'postponed'
                                : 'pending'
                            }`}
                          >
                            {status === 'CONFIRMED'
                              ? 'مؤكد'
                              : status === 'CANCELLED'
                              ? 'ملغي'
                              : status === 'POSTPONED'
                              ? 'مؤجل'
                              : 'بانتظار السداد'}
                          </span>
                        </td>
                        <td>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {/* View Details */}
                            <button
                              type="button"
                              className="btn-admin-outline text-xs py-1 px-2"
                              title="عرض تفاصيل التذكرة"
                              onClick={() => setSelectedBookingDetails(b)}
                            >
                              <Eye size={13} />
                              <span>عرض</span>
                            </button>

                            {/* Edit / Reschedule Modal Button */}
                            {status !== 'CANCELLED' && (
                              <button
                                type="button"
                                className="btn-admin-outline text-xs py-1 px-2 text-blue-700 border-blue-200 hover:bg-blue-50 flex items-center gap-1"
                                title="تعديل الموعد أو اليوم أو المقعد أو التأجيل"
                                onClick={() => setActionBooking(b)}
                              >
                                <Repeat size={13} />
                                <span>تعديل / تأجيل</span>
                              </button>
                            )}

                            {/* Direct WhatsApp Message */}
                            {studentPhone && (
                              <a
                                href={waUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 border border-emerald-200"
                                title={isConfirmed ? "إرسال تأكيد الحجز والبيانات للطالب عبر واتساب" : "مراسلة الطالب عبر واتساب"}
                              >
                                <MessageSquare size={13} />
                              </a>
                            )}

                            {/* Delete single booking */}
                            <button
                              type="button"
                              className="btn-admin-danger text-xs py-1 px-1.5"
                              title="حذف هذا السجل نهائياً"
                              onClick={() => handleDeleteBooking(b.id)}
                              disabled={actionLoading}
                              style={{
                                backgroundColor: '#fff',
                                color: '#E5232A',
                                borderColor: '#FECDD3',
                              }}
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            {/* Load More Footer (15 initial + 10 more on button click) */}
            {filteredBookings.length > 0 && (
              <div style={{ padding: '0 12px 14px' }}>
                <AdminLoadMore
                  currentCount={Math.min(visibleCount, filteredBookings.length)}
                  totalCount={filteredBookings.length}
                  itemName="حجز"
                  onLoadMore={() => setVisibleCount((prev) => prev + 10)}
                  step={10}
                  onShowAll={() => setVisibleCount(filteredBookings.length)}
                />
              </div>
            )}
          </div>
        </>
      )}

      {/* MODAL 1: Direct Booking for Student Modal */}
      {isDirectBookingModalOpen && (
        <AdminDirectBookingModal
          students={students}
          trips={trips}
          universities={universities}
          defaultDate={directBookingDefaultDate}
          defaultStudentUid={directBookingDefaultStudentUid}
          onClose={() => {
            setIsDirectBookingModalOpen(false);
            setDirectBookingDefaultStudentUid(undefined);
          }}
          onSuccess={(msg) => {
            setMessage({ text: msg, type: 'success' });
            loadAllData();
            setTimeout(() => setMessage(null), 5000);
          }}
        />
      )}

      {/* MODAL 2: Trip Passenger Manifest & Seat Control Modal */}
      {selectedTripForManifest && (
        <AdminTripPassengerModal
          trip={selectedTripForManifest}
          allTrips={trips}
          students={students}
          onClose={() => setSelectedTripForManifest(null)}
          onTripUpdated={() => {
            loadAllData();
          }}
        />
      )}

      {/* MODAL 3: Action / Reschedule / Postpone Modal for single booking */}
      {actionBooking && (
        <AdminBookingActionModal
          booking={actionBooking}
          allTrips={trips}
          onClose={() => setActionBooking(null)}
          onSuccess={(msg) => {
            setMessage({ text: msg, type: 'success' });
            loadAllData();
            setTimeout(() => setMessage(null), 5000);
          }}
        />
      )}

      {/* MODAL 4: Full Booking Details Viewer */}
      {selectedBookingDetails && (
        <div className="admin-modal-overlay" onClick={() => setSelectedBookingDetails(null)}>
          <div className="admin-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>تفاصيل الحجز والتذكرة #{selectedBookingDetails.bookingCode}</h3>
              <button
                type="button"
                className="admin-modal-close-btn"
                onClick={() => setSelectedBookingDetails(null)}
              >
                <X size={20} />
              </button>
            </div>

            <div className="admin-modal-body">
              {/* Top Banner */}
              <div className="bg-slate-50 p-4 rounded-xl border flex items-center justify-between">
                <div>
                  <span className="text-xs text-slate-500 block">رقم التذكرة الأمني:</span>
                  <strong className="font-mono text-base text-[#E5232A]">
                    {selectedBookingDetails.ticketId}
                  </strong>
                </div>
                <div className="text-left">
                  <span className="text-xs text-slate-500 block">حالة الحجز:</span>
                  <span
                    className={`status-pill ${
                      (selectedBookingDetails.status || selectedBookingDetails.bookingStatus) ===
                      'CONFIRMED'
                        ? 'confirmed'
                        : (selectedBookingDetails.status || selectedBookingDetails.bookingStatus) ===
                          'CANCELLED'
                        ? 'cancelled'
                        : (selectedBookingDetails.status || selectedBookingDetails.bookingStatus) ===
                          'POSTPONED'
                        ? 'postponed'
                        : 'pending'
                    }`}
                  >
                    {selectedBookingDetails.status || selectedBookingDetails.bookingStatus}
                  </span>
                </div>
              </div>

              {/* Grid Info */}
              <div className="form-grid-2">
                <div className="admin-form-group">
                  <label>اسم الطالب المسافر:</label>
                  <div className="p-2.5 bg-slate-50 rounded-lg text-slate-800 font-bold">
                    {selectedBookingDetails.studentSnapshot?.fullName || 'طالب مسجل'}
                  </div>
                </div>

                <div className="admin-form-group">
                  <label>رقم هاتف الطالب:</label>
                  <div
                    className="p-2.5 bg-slate-50 rounded-lg font-mono text-slate-800"
                    dir="ltr"
                  >
                    {selectedBookingDetails.studentSnapshot?.phoneNumber || 'غير مسجل'}
                  </div>
                </div>

                <div className="admin-form-group">
                  <label>الجامعة الوجهة:</label>
                  <div className="p-2.5 bg-slate-50 rounded-lg text-slate-800">
                    {selectedBookingDetails.universityName}
                  </div>
                </div>

                <div className="admin-form-group">
                  <label>تاريخ ووقت التحرك:</label>
                  <div className="p-2.5 bg-slate-50 rounded-lg text-slate-800 font-mono">
                    {selectedBookingDetails.tripDate} — {selectedBookingDetails.departureTime}
                  </div>
                </div>

                <div className="admin-form-group">
                  <label>رقم المقعد بالحافلة:</label>
                  <div className="p-2.5 bg-slate-50 rounded-lg text-[#E5232A] font-bold">
                    المقعد رقم {selectedBookingDetails.seatNumber}
                  </div>
                </div>

                <div className="admin-form-group">
                  <label>المبلغ المدفوع / المطلوب:</label>
                  <div className="p-2.5 bg-slate-50 rounded-lg text-emerald-600 font-bold font-mono">
                    {selectedBookingDetails.price} ج.م ({selectedBookingDetails.paymentMethod})
                  </div>
                </div>
              </div>

              {selectedBookingDetails.adminNote && (
                <div className="p-3 bg-amber-50 rounded-xl border border-amber-200 text-amber-900 text-xs">
                  <strong>ملاحظة إدارية:</strong> {selectedBookingDetails.adminNote}
                </div>
              )}

              {selectedBookingDetails.ticketSecurityHash && (
                <div className="p-3 bg-purple-50 rounded-xl border border-purple-200 text-purple-900 text-xs flex items-center gap-2">
                  <QrCode size={18} />
                  <span>
                    توقيع الأمان المشفر للتذكرة:
                    <code className="font-mono">
                      {selectedBookingDetails.ticketSecurityHash.substring(0, 24)}...
                    </code>
                  </span>
                </div>
              )}
            </div>

            <div className="admin-modal-footer">
              <button
                type="button"
                className="btn-admin-outline"
                onClick={() => setSelectedBookingDetails(null)}
              >
                إغلاق النافذة
              </button>

              <button
                type="button"
                className="btn-admin-primary flex items-center gap-1.5"
                onClick={() => {
                  const b = selectedBookingDetails;
                  setSelectedBookingDetails(null);
                  setActionBooking(b);
                }}
              >
                <Repeat size={16} />
                <span>تعديل أو تأجيل الرحلة</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
